import { all, take, race, put, takeEvery, select } from 'redux-saga/effects'
import _ from 'lodash'

import { getLocale, getToken, getCustomData } from '@global/selectors'
import config from '@config'

import { BLOCK_DATA_LOADED } from '../../../../../../../components/Workspace/components/HocBlock/constants'
import {
    blockDataTransformed,
    blockSaveCustomData,
} from '../../../../../../../components/Workspace/components/HocBlock/actions'

import {
    ACTIVITIES_TRADES_TABLE_CHANGE,
    ACTIVITIES_TRADES_TABLE_CHANGE_REQUEST,
    ACTIVITIES_TRADES_TABLE_DATA_RECEIVED,
    ACTIVITIES_TRADES_TABLE_LOADING_TRUE,
    ACTIVITIES_TRADES_TABLE_LOADING_FALSE,
} from './components/Trades/constants'

import {
    activitiesTradesTableChangeRequest,
    activitiesTradesTableDataReceived,
    activitiesTradesTableLoadingTrue,
    activitiesTradesTableLoadingFalse,
} from './components/Trades/actions'

const ACTIVITIES_TRADES_TABLE_CHANGE_REQUEST_SUCCESS = (id) => `${ACTIVITIES_TRADES_TABLE_CHANGE_REQUEST(id)}_SUCCESS`
const ACTIVITIES_TRADES_TABLE_CHANGE_REQUEST_FAILED = (id) => `${ACTIVITIES_TRADES_TABLE_CHANGE_REQUEST(id)}_FAILED`

function* onDataLoaded(_action) {
    const blockKey = _.get(_action, 'data.blockKey', null)
    if (blockKey !== config.modules.report.blocks.activities.key) return null

    const module = _.get(_action, 'data.module', null)
    const id = _.get(_action, 'data.id', null)
    if (!module || !id || !blockKey) return null

    const data = _.get(_action, 'data.data', null)

    // Transformation start
    const transformedData = {
        counts: _.get(data, '[0].counts', {}),
        summaryTables: _.reduce(
            _.get(data, '[0].mainActivities'),
            (acc, val) => [
                ...acc,
                {
                    _table: [
                        {
                            key: _.get(val, 'key', null),
                            title: _.get(val, 'title', null),
                            tables: _.get(val, 'tables', []),
                        },
                    ],
                },
            ],
            []
        ),
        secondaryNace: {
            secondaryNaceTable: {
                _table: [
                    {
                        key: 'secondaryNaceTable',
                        title: 'activitiesTitle',
                        tables: [
                            {
                                nested: false,
                                rows: _.get(data, '[1].naceActivities', []),
                            },
                        ],
                    },
                ],
            },
        },
        secondarySic: {
            secondarySicTable: {
                _table: [
                    {
                        key: 'secondarySicTable',
                        title: 'activitiesTitle',
                        tables: [
                            {
                                nested: false,
                                rows: _.get(data, '[1].sicActivities', []),
                            },
                        ],
                    },
                ],
            },
        },
    }
    // Transformation end

    yield put(blockDataTransformed(module, id, blockKey, transformedData))

    yield put(
        blockSaveCustomData(module, id, blockKey, {
            isLoading: false,
            loaded: true,
            data: data?.[2]?.data ?? [],
            pagination: data?.[2]?.query?.pagination ?? {},
            sorted: data?.[2]?.query?.sort?.sortedColumns ?? [],
            filter: data?.[2]?.query?.filter ?? {},
        })
    )

    return null
}

function* activitiesTradesTableSaga(action) {
    const module = action?.data?.module
    const blockKey = action?.data?.blockKey
    const id = action?.data?.id
    const level = action?.data?.level
    const queryParams = action?.data?.queryParams

    const locale = yield select(getLocale)
    const token = yield select(getToken, id, module)

    const lang = config.languages[locale].requestKey

    if (!module || !blockKey || !id || !level) return null

    yield put(activitiesTradesTableLoadingTrue(module, id, blockKey, { isLoading: true }))

    const filters = _.reduce(
        queryParams?.filtered,
        (acc, filter) => ({
            ...acc,
            [`${filter.id}[]`]: filter.value,
        }),
        {}
    )

    const qp = {
        'sort[]': (queryParams?.sorted ?? []).map((val) => `${val?.id}${val.desc ? ':desc' : ':asc'}`),
        page: queryParams?.pagination?.page,
        rows: queryParams?.pagination?.rowsPerPage,
        ...filters,
    }

    yield put(activitiesTradesTableChangeRequest(module, blockKey, id, level, lang, token, qp))

    const response = yield race({
        success: take(ACTIVITIES_TRADES_TABLE_CHANGE_REQUEST_SUCCESS(id)),
        failed: take(ACTIVITIES_TRADES_TABLE_CHANGE_REQUEST_FAILED(id)),
    })

    if (!response.failed) {
        const responseBody = response?.success?.payload?.response?.body ?? {}
        const data = responseBody?.data ?? []
        const query = responseBody?.query ?? {}
        const pagination = query?.pagination ?? {}
        const filter = query?.filter ?? {}
        const sorted = _.reduce(
            query?.sort?.sortedColumns,
            (acc, val) => [
                ...acc,
                {
                    id: val?.columm, // TYPO FROM BACKEND
                    desc: val?.direction === 'desc',
                },
            ],
            []
        )

        const newCustomData = {
            data,
            pagination,
            sorted,
            filter,
            isLoading: false,
        }

        yield put(activitiesTradesTableDataReceived(module, id, blockKey, newCustomData))
    }

    yield put(activitiesTradesTableLoadingFalse(module, id, blockKey, { isLoading: false }))

    return null
}

function* activitiesChangeCustomDataCallSaga(action) {
    const blockKey = action?.data?.blockKey
    if (blockKey !== config.modules.report.blocks.activities.key) return null

    const module = action?.data?.module
    const id = action?.data?.id
    if (!module || !id || !blockKey) return null

    const customData = yield select(getCustomData, module, blockKey, id)
    const incomingChange = action?.data?.customData

    const newCustomData = {
        ...customData,
        ...incomingChange,
    }

    yield put(blockSaveCustomData(module, id, blockKey, newCustomData))

    return null
}

export default function* blockTransformSaga() {
    yield all([
        takeEvery(BLOCK_DATA_LOADED, onDataLoaded),
        takeEvery(ACTIVITIES_TRADES_TABLE_CHANGE, activitiesTradesTableSaga),
        takeEvery(ACTIVITIES_TRADES_TABLE_DATA_RECEIVED, activitiesChangeCustomDataCallSaga),
        takeEvery(ACTIVITIES_TRADES_TABLE_LOADING_TRUE, activitiesChangeCustomDataCallSaga),
        takeEvery(ACTIVITIES_TRADES_TABLE_LOADING_FALSE, activitiesChangeCustomDataCallSaga),
    ])
}
